import React, { FC } from 'react';
import {
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Box,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { TransitionProps } from '@material-ui/core/transitions';
import { PageTextContentType } from '../../../hooks/useContentMgmt';
import Buttons from '../../buttons';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import {
  ApplicationEv,
  CreateApplicationEvInput,
  CreateApplicationEvOutput,
  IneligibilityReasonCode,
} from '../../../__generated__/pgeplus-types';
import { FetchResult } from 'apollo-link';
import { Link, navigate } from 'gatsby';
import routes from '../../../routes';
import useUtil from '../../../hooks/pge-plus/useUtil';
import { AccountDetail } from '../../../__generated__/pge-types';
import { EVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import IconCharger from '../../pge-plus-common/static/chargerIcon.svg';
import IconInstaller from '../../pge-plus-common/static/installerIcon.svg';
import IconExpand from '../../pge-plus-common/static/Expand.svg';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children?: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
  content: PageTextContentType;
  open: boolean;
  onClose: () => void;
  createApplication: (
    input: CreateApplicationEvInput,
  ) => Promise<
    FetchResult<{
      createApplicationEV: CreateApplicationEvOutput;
    }>
  >;
  personId: string;
  premiseId: string;
  account?: AccountDetail;
  updateFormState: <T extends EVChargerFormState, K extends keyof T>(
    key: K,
    data: T[K],
  ) => boolean;
  initFormStateAndNavigate: (
    application: ApplicationEv,
    hasBYOC?: boolean | undefined,
  ) => void;
};

const BYOCConfirmModal: FC<Props> = ({
  open,
  content,
  onClose,
  createApplication,
  personId,
  premiseId,
  account,
  updateFormState,
  initFormStateAndNavigate,
}: Props) => {
  const theme = useTheme();
  const { wrapWithLoader } = useWrapWithLoader();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { setErrorNotification } = useUtil();

  const createApp = wrapWithLoader(async (suppliedByCustomer: boolean) => {
    try {
      const { data } = await createApplication({
        personId,
        premiseId,
        accountId: account?.accountNumber || '',
        suppliedByCustomer,
      });
      onClose();
      const ineligibilityReasons =
        data?.createApplicationEV?.ineligibilityReasons;
      const error =
        ineligibilityReasons &&
        ineligibilityReasons.length > 0 &&
        ineligibilityReasons.filter(
          reason =>
            reason.code !==
            IneligibilityReasonCode.IneligibleApplicationInProgress,
        );

      if (error) {
        void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
          state: {
            IneligibilityCode: ineligibilityReasons[0]?.code,
            IneligibilityNumber: ineligibilityReasons[0]?.reference?.number,
            fromPage: routes.PGE_PLUS_EV_CHARGERS_CHOOSE_ACCOUNT,
          },
        });
      }

      if (data?.createApplicationEV?.application) {
        //Update the selected account Form State
        account && updateFormState('account', account);
        initFormStateAndNavigate(data?.createApplicationEV?.application);
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  return (
    <Dialog
      open={open}
      TransitionComponent={isMobile ? Transition : undefined}
      onClose={onClose}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      scroll="body"
      fullWidth={true}
      disableRestoreFocus={true}
      PaperProps={{
        style: {
          maxWidth: '650px',
          margin: 0,
        },
      }}
    >
      <Paper
        css={{
          [theme.breakpoints.down('xs')]: {
            maxWidth: '600px !important',
            width: '100%',
            position: 'fixed',
            bottom: 0,
            left: 0,
          },
        }}
      >
        <DialogTitle css={{ padding: '24px 24px 0' }}>
          <Grid
            container
            css={{
              justifyContent: 'end',
            }}
          >
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={onClose}
              css={{
                padding: 0,
              }}
            >
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent
          css={{
            padding: '8px 24px 40px',
            [theme.breakpoints.down('xs')]: {
              padding: '0 24px 40px',
            },
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Typography variant={'h1'} css={{ marginBottom: '20px' }}>
                {content.get('LET_GET_STARTED')}
              </Typography>
            </Grid>

            <Grid container direction="row" spacing={1}>
              <Box
                css={{ width: '49%', padding: theme.spacing(2) }}
                justifyContent={'center'}
                display={'flex'}
                alignItems={'flex-start'}
              >
                <Grid
                  container
                  direction="column"
                  css={{
                    justifyContent: 'space-between',
                    height: '100%',
                  }}
                >
                  <Grid container direction="row">
                    <Grid item xs={3} md={3}>
                      <IconCharger />
                    </Grid>
                    <Grid
                      item
                      xs={1}
                      md={1}
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconExpand />
                    </Grid>
                    <Grid item xs={3} md={3}>
                      <IconInstaller />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant={'h2'}>
                        {content.get('NEED_CHARGER_SUBTEXT')}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item>
                    <Buttons
                      type="button"
                      color={'primary'}
                      fullWidth
                      variant="contained"
                      onClick={() => createApp(false)}
                      css={{
                        fontSize: '18px',
                        borderRadius: '30px',
                        marginTop: '20px',
                      }}
                    >
                      {content.get('NEED_A_CHARGER')}
                    </Buttons>
                  </Grid>
                </Grid>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                css={{ margin: theme.spacing(1, 0, 0, 0) }}
              />
              <Box
                css={{ width: '49%', padding: theme.spacing(2) }}
                justifyContent={'center'}
                display={'flex'}
                alignItems={'flex-start'}
              >
                <Grid
                  container
                  direction="column"
                  css={{ justifyContent: 'space-between', height: '100%' }}
                >
                  <Grid container direction="row">
                    <Grid item xs={12}>
                      <IconInstaller />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant={'h2'}>
                        {content.get('HAVE_CHARGER_SUBTEXT')}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Buttons
                      type="button"
                      fullWidth
                      color={'primary'}
                      variant="contained"
                      onClick={() => createApp(true)}
                      css={{
                        fontSize: '18px',
                        borderRadius: '30px',
                        marginTop: '20px',
                      }}
                    >
                      {content.get('HAVE_A_CHARGER')}
                    </Buttons>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              css={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '10px',
              }}
            >
              <Link to={routes.PGE_PLUS_LANDING}>
                {content.get('REUSABLE_LEARN_MORE')}
              </Link>
            </Grid>
          </Grid>
        </DialogContent>
      </Paper>
    </Dialog>
  );
};

export default BYOCConfirmModal;
